/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */
/**
 * reconnectService
 *
 * This version of implementation is for DPM, and could be replaced,
 * once the WS1 changes the invoke workflow
 *
 * And we want to keep related data and logic all sealed inside this server,
 * to ease the effort to remove it in the future.
 *
 *
 *
 * safeStorageService had been removed, not bring it back since chrome app
 * would be discarded soon, use sessionStorage directly here.
 *
 *
 * We could move other code here when refactor, but it's not a goal for
 * this patch.
 */

import { Injectable } from "@angular/core";
import Logger from "../../../core/libs/logger";
import { ViewClientModel } from "../../../shared/common/model/viewclient-model";
import { ConnectionURIModel } from "../../../shared/common/model/connection-uri-model";
import { RemoteSessionEventService } from "../../common/remote-session/remote-session-event.service";
import { setWS1SessionStr, getWS1SessionStr, clearWS1SessionStr } from "../../../core/libs";
import { BusEvent, EventBusService } from "@html-core";
import { WindowRefService } from "../../common/service/window-ref.service";

@Injectable({
   providedIn: "root"
})
export class ReconnectService {
   private static MAX_SUSPEND_TIME = 2 * 60 * 1000; //2 mins

   private inited: boolean = false;
   private disabled: boolean = false;
   private beingDestroyed: boolean = false;
   private appendBeforeReconnect: boolean = false;

   private horizonId: string = null;
   private reconnectDesktopSessions: Function = null;
   private reconnectAppSessions: Function = null;

   constructor(
      private viewClientModel: ViewClientModel,
      private connectionURIModel: ConnectionURIModel,
      private remoteSessionEventService: RemoteSessionEventService,
      private eventBusService: EventBusService,
      private windowRefService: WindowRefService
   ) {
      this.windowRefService.nativeWindow.addEventListener("beforeunload", () => {
         this.beingDestroyed = true;
      });
   }
   // Chrome Client should not call init.
   public init = (initParam) => {
      if (!initParam.horizonId) {
         Logger.debug("skip init reconnectService for non-WS1 launches");
         return;
      }
      this.inited = true;
      this.appendBeforeReconnect = true;
      /**
       * For avoid client behavior change for F5, see details in bug 2579385
       */
      this.disabled = !!this.viewClientModel.disableReconnectForWS1 || this.connectionURIModel.isF5Session();
      this.horizonId = initParam.horizonId;
      this.reconnectDesktopSessions = initParam.reconnectDesktopSessions;
      this.reconnectAppSessions = initParam.reconnectAppSessions;
      this.remoteSessionEventService.addEventListener("sessionPropertyUpdate", this.onSessionPropertyUpdate);

      this.eventBusService
         .listen(BusEvent.TabMergeRequest.MSG_TYPE)
         .subscribe(async (event: BusEvent.TabMergeRequest) => {
            // get the list and fire another event as response.
            try {
               let previousWS1SessionInfo: any = await this.getTargetSessions();
               this.eventBusService.dispatch(new BusEvent.TabMergeResponse(previousWS1SessionInfo));
            } catch (e) {
               this.eventBusService.dispatch(new BusEvent.TabMergeResponse(null));
               Logger.error("cancel tab merge due to: " + e);
            }
         });
   };

   public onSessionPropertyUpdate = (skip: boolean, info) => {
      this.sync(info);
   };

   public isIncludedIn = (targetItem, poolArray) => {
      return poolArray.some((item) => {
         const findTarget = item.key === targetItem.key;
         if (findTarget) {
            Logger.debug("find duplicated record for: " + item.key);
         }
         return findTarget;
      });
   };

   private merge = (newList) => {
      return new Promise((resolve, reject) => {
         if (!this.appendBeforeReconnect) {
            Logger.debug("skip merge since appendBeforeReconnect is false");
            resolve(newList);
            return;
         }
         if (!newList || newList.length === 0) {
            Logger.debug("skip merge since newList is empty");
            resolve([]);
            return;
         }
         const currentTime = new Date().getTime();
         newList.forEach((item) => {
            item.time = currentTime;
         });
         this.getTargetSessions()
            .then((previousWS1SessionInfo: any) => {
               if (!previousWS1SessionInfo || !previousWS1SessionInfo.desktopSessions) {
                  Logger.debug("no previous WS1 session, use the new list");
                  resolve(newList);
                  return;
               }
               previousWS1SessionInfo.desktopSessions.forEach((item) => {
                  if (currentTime - item.time <= ReconnectService.MAX_SUSPEND_TIME) {
                     if (!this.isIncludedIn(item, newList)) {
                        item.time = currentTime;
                        newList.push(item);
                     }
                  }
               });
               resolve(newList);
            })
            .catch((e) => {
               Logger.debug("failed to get previous WS1 session, use the new list");
               Logger.debug(e);
               resolve(newList);
            });
      });
   };

   public sync = (sessions) => {
      if (this.disabled) {
         Logger.info("don't sync for WS1 session since reconnect for WS1 has been disabled");
         return;
      }
      if (!this.inited) {
         Logger.debug("skip session sync since server not init");
         return;
      }
      const currentDesktopSessions = sessions.filter((session) => !session.isApplicationSession && session.isConnected);
      this.merge(currentDesktopSessions).then((mergedDesktopSessions) => {
         setTimeout(() => {
            if (!this.beingDestroyed) {
               setWS1SessionStr(
                  JSON.stringify({
                     horizonId: this.horizonId,
                     desktopSessions: mergedDesktopSessions
                  })
               );
            }
         }, 100);
      });
   };

   public getTargetSessions = () => {
      return new Promise((resolve, reject) => {
         try {
            let previousWS1SessionStr = getWS1SessionStr();

            if (!previousWS1SessionStr) {
               reject("There is no previous WS1 sessions to recovery");
               return;
            }
            let previousWS1SessionInfo = JSON.parse(previousWS1SessionStr);
            if (!previousWS1SessionInfo.desktopSessions) {
               reject("There is no previous WS1 sessions to recovery");
               return;
            }
            if (previousWS1SessionInfo.horizonId !== this.horizonId) {
               reject("detect WS1 session changed, not reconnect WS1 sessions");
               return;
            }
            resolve(previousWS1SessionInfo);
         } catch (e) {
            reject("Failed to parse WS1 session");
         }
      });
   };

   public reconnectForHWS = async (excludeId, needReconnectApp) => {
      if (this.disabled) {
         Logger.info("don't reconnect for WS1 session since reconnect for WS1 has been disabled");
         return;
      }
      if (!this.inited) {
         throw "Not a WS1 session";
      }
      let previousWS1SessionInfo: any = await this.getTargetSessions();
      this.appendBeforeReconnect = false;
      const targetSessions = previousWS1SessionInfo.desktopSessions.filter((session) => session.key !== excludeId);

      await this.reconnectDesktopSessions(targetSessions);
      if (needReconnectApp) {
         await this.reconnectAppSessions();
      }
   };

   public clear = () => {
      clearWS1SessionStr();
   };
}
